import { useState } from 'react'
import { supabase } from '../supabaseClient'
import { PDFDocument, rgb } from 'pdf-lib'

const BLUE = '#185FA5'
const GREEN = '#2e7d32'

export default function AIMatchAnalysisModal({ fixture, squadName, clubName, onClose, currentUserEmail }) {
  const [analysis, setAnalysis] = useState(fixture.match_analysis || null)
  const [kickoutCaveat, setKickoutCaveat] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editText, setEditText] = useState('')
  const [saving, setSaving] = useState(false)
  const [exportingPdf, setExportingPdf] = useState(false)
  const [emailState, setEmailState] = useState(null)

  async function generateAnalysis() {
    setLoading(true)
    setError(null)
    try {
      const { data: events } = await supabase
        .from('match_events')
        .select('*, players(full_name)')
        .eq('fixture_id', fixture.id)
        .order('minute', { ascending: true })

      const { data: lineupRows } = await supabase
        .from('lineups')
        .select('*, players(full_name)')
        .eq('fixture_id', fixture.id)

      const starters = (lineupRows || [])
        .filter(l => l.starting)
        .map(l => ({
          player_id: l.player_id,
          player_name: l.players?.full_name || 'Unknown',
          position_number: Number(l.position_override || l.match_number) || null,
        }))

      const subOns = (events || []).filter(e => e.event_type === 'sub_on')
      const subOffs = (events || []).filter(e => e.event_type === 'sub_off')
      const subs = subOns.map(on => {
        const off = subOffs.find(o => o.minute === on.minute) || {}
        const offLineup = (lineupRows || []).find(l => l.player_id === off.player_id)
        return {
          minute: on.minute,
          player_on_name: on.players?.full_name || 'Unknown',
          position_number: Number(offLineup?.position_override || offLineup?.match_number) || null,
        }
      })

      const oppEvents = (events || []).filter(e =>
        ['opp_goal', 'opp_point', 'opp_twopoint', 'opp_card'].includes(e.event_type)
      )

      const { data: oppPlayers } = await supabase
        .from('opposition_players')
        .select('jersey_number, full_name')
        .eq('fixture_id', fixture.id)

      const { data: recentFixtures } = await supabase
        .from('fixtures')
        .select('opponent, home_goals, home_points, away_goals, away_points, fixture_date')
        .eq('squad_id', fixture.squad_id)
        .eq('team_number', fixture.team_number)
        .eq('sport', fixture.sport)
        .eq('status', 'completed')
        .neq('id', fixture.id)
        .order('fixture_date', { ascending: false })
        .limit(5)

      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('https://mrjtwgvscgcvuecxjcbg.supabase.co/functions/v1/generate-match-analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + session.access_token },
        body: JSON.stringify({
          fixture: {
            id: fixture.id, opponent: fixture.opponent, sport: fixture.sport,
            home_goals: fixture.home_goals, home_points: fixture.home_points,
            away_goals: fixture.away_goals, away_points: fixture.away_points,
          },
          squadName, clubName,
          events: (events || []).map(e => ({
            event_type: e.event_type, minute: e.minute, notes: e.notes,
            player_name: e.players?.full_name || null,
          })),
          starters, subs,
          oppEvents: oppEvents.map(e => ({
            event_type: e.event_type, minute: e.minute, opp_jersey_number: e.opp_jersey_number,
          })),
          oppPlayers: oppPlayers || [],
          recentFixtures: recentFixtures || [],
        }),
      })
      if (!res.ok) throw new Error('Failed to generate analysis')
      const data = await res.json()
      setAnalysis(data.analysis)
      setKickoutCaveat(data.kickoutCaveat)
      await supabase.from('fixtures').update({ match_analysis: data.analysis }).eq('id', fixture.id)
    } catch (err) {
      setError('Failed to generate analysis. Please try again.')
    }
    setLoading(false)
  }

  async function copyToClipboard() {
    if (!analysis) return
    await navigator.clipboard.writeText(analysis)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function shareWhatsApp() {
    const text = encodeURIComponent(
      'Match Analysis: ' + (clubName || '') + ' ' + squadName + ' vs ' + fixture.opponent + '\n\n' + (analysis || '')
    )
    window.open('https://wa.me/?text=' + text, '_blank')
  }


  async function buildPdfBytes() {
    const pdfDoc = await PDFDocument.create()
      const page = pdfDoc.addPage([595, 842])
      const font = await pdfDoc.embedFont('Helvetica')
      const boldFont = await pdfDoc.embedFont('Helvetica-Bold')
      let y = 800

      page.drawText('GAA Match Tracker', { x: 50, y, size: 10, font: boldFont, color: rgb(0.09, 0.37, 0.65) })
      y -= 20
      page.drawText((clubName || '') + ' ' + squadName + ' vs ' + fixture.opponent, { x: 50, y, size: 16, font: boldFont })
      y -= 20
      page.drawText('Coach Match Analysis', { x: 50, y, size: 11, font, color: rgb(0.4, 0.4, 0.4) })
      y -= 30

      let currentPage = page
      function newPage() {
        currentPage = pdfDoc.addPage([595, 842])
        y = 800
      }

      const lines = (analysis || '').split('\n')
      for (const line of lines) {
        const words = line.split(' ')
        let current = ''
        for (const word of words) {
          const test = current ? current + ' ' + word : word
          if (font.widthOfTextAtSize(test, 11) > 495) {
            if (y < 60) newPage()
            currentPage.drawText(current, { x: 50, y, size: 11, font, color: rgb(0.2, 0.2, 0.2) })
            y -= 16
            current = word
          } else {
            current = test
          }
        }
        if (current) {
          if (y < 60) newPage()
          currentPage.drawText(current, { x: 50, y, size: 11, font, color: rgb(0.2, 0.2, 0.2) })
          y -= 16
        }
        y -= 4
        if (y < 60) newPage()
      }

      currentPage.drawText('Generated by GAA Match Tracker · gaamatchtracker.ie', { x: 50, y: 40, size: 9, font, color: rgb(0.6, 0.6, 0.6) })

      return await pdfDoc.save()
  }

  async function openPdf() {
    setExportingPdf(true)
    try {
      const pdfBytes = await buildPdfBytes()
      const blob = new Blob([pdfBytes], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
    } catch (e) {
      setError('Failed to open PDF.')
    }
    setExportingPdf(false)
  }

  async function sharePdf() {
    setExportingPdf(true)
    try {
      const pdfBytes = await buildPdfBytes()
      const blob = new Blob([pdfBytes], { type: 'application/pdf' })
      const fileName = squadName + ' vs ' + fixture.opponent + ' - Analysis.pdf'
      const file = new File([blob], fileName, { type: 'application/pdf' })

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'Match Analysis: ' + squadName + ' vs ' + fixture.opponent,
        })
      } else {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        a.click()
        URL.revokeObjectURL(url)
      }
    } catch (e) {
      if (e.name !== 'AbortError') setError('Failed to share PDF.')
    }
    setExportingPdf(false)
  }

  async function sendEmail() {
    setEmailState('sending')
    try {
      const htmlBody = `
        <div style="font-family: sans-serif; max-width: 520px; margin: 0 auto;">
          <div style="background: linear-gradient(135deg, #0d3d6b 0%, #185FA5 100%); padding: 24px; border-radius: 12px 12px 0 0; text-align: center; color: white;">
            <p style="font-size: 11px; opacity: 0.7; letter-spacing: 1px; margin: 0 0 4px">COACH MATCH ANALYSIS</p>
            <h2 style="margin: 0 0 4px; font-size: 20px;">${squadName} vs ${fixture.opponent}</h2>
          </div>
          <div style="background: white; border: 1px solid #e0e0e0; border-top: none; border-radius: 0 0 12px 12px; padding: 20px;">
            <div style="font-size: 14px; line-height: 1.8; color: #333; white-space: pre-wrap;">${analysis}</div>
            <p style="font-size: 11px; color: #bbb; text-align: center; margin-top: 20px;">GAA Match Tracker · gaamatchtracker.ie</p>
          </div>
        </div>`
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('https://mrjtwgvscgcvuecxjcbg.supabase.co/functions/v1/send-invite-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + session.access_token },
        body: JSON.stringify({
          type: 'match_analysis',
          toEmails: [currentUserEmail],
          subject: 'Match Analysis: ' + squadName + ' vs ' + fixture.opponent,
          htmlBody,
        }),
      })
      if (!res.ok) throw new Error('Failed')
      setEmailState('sent')
    } catch (e) {
      setEmailState('error')
    }
  }

  const totalHome = (fixture.home_goals * 3) + fixture.home_points
  const totalAway = (fixture.away_goals * 3) + fixture.away_points
  const result = totalHome > totalAway ? 'Win' : totalHome < totalAway ? 'Loss' : 'Draw'

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 520, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>

        <div style={{ background: 'linear-gradient(135deg, #0d3d6b 0%, #185FA5 100%)', color: '#fff', padding: '20px 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 11, opacity: 0.7, letterSpacing: 1, marginBottom: 4 }}>COACH MATCH ANALYSIS · NOT SHARED WITH SUPPORTERS</div>
              <div style={{ fontSize: 17, fontWeight: 700 }}>{squadName} vs {fixture.opponent}</div>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>
                {fixture.home_goals}-{String(fixture.home_points).padStart(2, '0')} ({totalHome})  v  {fixture.away_goals}-{String(fixture.away_points).padStart(2, '0')} ({totalAway}) — {result}
              </div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', opacity: 0.7 }}>×</button>
          </div>
        </div>

        <div style={{ padding: 16 }}>
          {error && (
            <div style={{ background: '#fff3f3', border: '1px solid #f5c6cb', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#c0392b', fontSize: 14 }}>
              {error}
            </div>
          )}

          {!analysis && !loading && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🧠</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#333', marginBottom: 8 }}>Generate Match Analysis</div>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 24 }}>
                AI will analyse the recorded events, opposition threats, and recent form for a detailed coach-only breakdown of the match.
              </div>
              <button
                onClick={generateAnalysis}
                style={{ padding: '12px 28px', background: BLUE, color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}
              >
                🧠 Generate Analysis
              </button>
            </div>
          )}

          {loading && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🧠</div>
              <div style={{ fontSize: 14, color: '#888' }}>Analysing match...</div>
            </div>
          )}

          {analysis && !loading && (
            <div>
              {kickoutCaveat && (
                <div style={{ background: '#fff8e1', border: '1px solid #ffd54f', borderRadius: 8, padding: '10px 14px', marginBottom: 12, color: '#7A5B10', fontSize: 12 }}>
                  ⚠️ {kickoutCaveat}
                </div>
              )}

              {editing ? (
                <div style={{ marginBottom: 16 }}>
                  <textarea
                    value={editText}
                    onChange={e => setEditText(e.target.value)}
                    rows={14}
                    style={{ width: '100%', padding: 12, border: '1px solid #ddd', borderRadius: 8, fontSize: 13, lineHeight: 1.7, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button
                      onClick={async () => {
                        setSaving(true)
                        await supabase.from('fixtures').update({ match_analysis: editText }).eq('id', fixture.id)
                        setAnalysis(editText)
                        setEditing(false)
                        setSaving(false)
                      }}
                      disabled={saving}
                      style={{ flex: 2, padding: '10px', background: BLUE, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, opacity: saving ? 0.6 : 1 }}
                    >
                      {saving ? 'Saving...' : '💾 Save changes'}
                    </button>
                    <button
                      onClick={() => setEditing(false)}
                      style={{ flex: 1, padding: '10px', background: '#f0f0f0', color: '#333', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 10, padding: 16, marginBottom: 16, fontSize: 14, lineHeight: 1.8, color: '#333', whiteSpace: 'pre-wrap' }}>
                    {analysis}
                  </div>

                  <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                    <button onClick={copyToClipboard} style={{ flex: 1, minWidth: 90, padding: '10px', background: copied ? '#e8f5e9' : '#f0f0f0', color: copied ? GREEN : '#333', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                      {copied ? '✅ Copied!' : '📋 Copy'}
                    </button>
                    <button onClick={shareWhatsApp} style={{ flex: 1, minWidth: 90, padding: '10px', background: '#e8f5e9', color: GREEN, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                      💬 WhatsApp
                    </button>
                    <button onClick={openPdf} disabled={exportingPdf} style={{ flex: 1, minWidth: 90, padding: '10px', background: '#f0f5ff', color: BLUE, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, opacity: exportingPdf ? 0.6 : 1 }}>
                      {exportingPdf ? '...' : '📄 Open PDF'}
                    </button>
                    <button onClick={sharePdf} disabled={exportingPdf} style={{ flex: 1, minWidth: 90, padding: '10px', background: '#f0f5ff', color: BLUE, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, opacity: exportingPdf ? 0.6 : 1 }}>
                      {exportingPdf ? '...' : '📤 Share PDF'}
                    </button>
                    <button onClick={() => { setEditText(analysis); setEditing(true) }} style={{ flex: 1, minWidth: 90, padding: '10px', background: '#fff8e1', color: '#e65100', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                      ✏️ Edit
                    </button>
                    <button onClick={generateAnalysis} style={{ flex: 1, minWidth: 90, padding: '10px', background: '#f0f0f0', color: '#555', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                      🔄 Regenerate
                    </button>
                  </div>

                  <div style={{ background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 10, padding: '12px 14px', marginBottom: 8 }}>
                    {emailState === 'sent' ? (
                      <p style={{ fontSize: 13, color: GREEN, textAlign: 'center', padding: 8 }}>✅ Emailed to you successfully</p>
                    ) : emailState === 'error' ? (
                      <p style={{ fontSize: 13, color: '#c0392b', textAlign: 'center', padding: 8 }}>❌ Failed to send. Please try again.</p>
                    ) : emailState === 'sending' ? (
                      <p style={{ fontSize: 13, color: '#888', textAlign: 'center', padding: 8 }}>Sending...</p>
                    ) : (
                      <button onClick={sendEmail} style={{ width: '100%', padding: '10px', background: BLUE, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                        📧 Email to myself
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          <button onClick={onClose} style={{ width: '100%', padding: 12, background: '#f0f0f0', color: '#333', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14, marginTop: 8 }}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}